import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import SectionsWritingLoader from './SectionsWritingLoader';
import ResponseLogger from '../ProposalPreparation/ResponseLogger';
import "../ProposalPreparation/ProposalPreparation.css";

import { createProject, getSectionNames, writeSection, saveProjectDate } from "./api";
import { extractAuthors } from "./utils";

const CAMPAIGN_DEADLINE = new Date('2026-04-15T23:59:59');

const todayDate = new Date().toISOString();
const BASE_SECTIONS = ['METHOD', 'KEYWORDS', 'PRIORITY', 'TASKS', 'PUBLICATIONS', 'MANAGEMENT'];
const TABLE_SECTIONS = ["RISKS", "PRIOR_WORK", "CONTRIBUTIONS", "SPECIFIC_TARGETS", "TASKS", "WP", "SUBTASKS"];

const SectionsWriting = () => { 
  const location = useLocation();
  const { user_idx, idea_idx, title, method, keywordsList, priority, wpList, publicationList, projectDuration } = location.state || {};
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [sectionNames, setSectionNames] = useState([]);
  const [responseLog, setResponseLog] = useState(null);
  const [error, setError] = useState(null);
  const [showNoCreditPopup, setShowNoCreditPopup] = useState(false); // new popup state
  const isCancelledRef = useRef(false);

  // --- KREDİ POPUP GERİ SAYIM STATELERİ ---
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const logResponse = (step, message) => {
    setResponseLog({ step, message });
  };

  const formatDateTR = (date) =>
    date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // Kampanya bitiş tarihine kadar geri sayım sayacı
  useEffect(() => {
    // Popup açık değilse sayacı boşuna çalıştırmamak için optimize edilebilir, 
    // ancak şu anki haliyle de oldukça hafif bir işlemdir.
    const countDownDate = CAMPAIGN_DEADLINE.getTime();

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = countDownDate - now;

      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);
  // ----------------------------------------

  useEffect(() => {
    isCancelledRef.current = false;

    const runSteps = async () => {
      try {
        // Step 1: Create the project
        let project_idx;
        try {
          project_idx = await runWithRetry(() =>
            createProject(user_idx, idea_idx, title, method, keywordsList, priority, wpList, publicationList.map(pub => ({
              title: pub.title,
              year: pub.year,
              authors: extractAuthors(pub.citation),
              abstract: pub.abstract
            })))
          );
          logResponse(0, JSON.stringify(`Proje oluşturuldu.`));
        } catch (err) {
          // Handle specific "no credit" error
          if (err.response?.status === 500 && err.response?.data?.detail?.includes("Do not have any credit")) {
            setShowNoCreditPopup(true);
            return;
          } else {
            throw err;
          }
        }

        if (isCancelledRef.current) return;

        // Step 2: Get section names
        const sections_dict = await runWithRetry(() => getSectionNames());
        const sec_keys = Object.keys(sections_dict);
        const sec_names = Object.values(sections_dict);
        setSectionNames(sec_names);

        logResponse(1, JSON.stringify(`Bölümler hazırlanıyor...`));
        await runWithRetry(() => saveProjectDate(project_idx, todayDate, projectDuration), 3000);

        if (isCancelledRef.current) return;

        // Step 3: Write each section
        const collectedSectionsContent = {};
      
        for (let i = 0; i < sec_keys.length; i++) {
          if (!BASE_SECTIONS.includes(sec_keys[i])) {
            setCurrentStep(i - 1);
            const sectionContent = await runWithRetry(() => writeSection(project_idx, sec_keys[i], false), 3000);
            collectedSectionsContent[sec_keys[i]] = sectionContent;

            if (TABLE_SECTIONS.includes(sec_keys[i])) {
              logResponse(i + 2, JSON.stringify(JSON.parse(sectionContent)));
            } else {
              logResponse(i + 2, JSON.stringify(sectionContent));
            }
          }

          if (isCancelledRef.current) return;
        }

        if (isCancelledRef.current) return;

        navigate('/project_writing_user', {
          state: {
            project_idx,
            title,
            method,
            priority,
            wpList,
            sectionContent: collectedSectionsContent
          }
        });

      } catch (err) {
        if (!isCancelledRef.current) {
          console.error("Step error:", err);
          setError('Beklenmeyen bir hata ile karşılaşıldı. Lütfen daha sonra tekrar deneyin.');
        }
      }
    };

    runSteps();

    return () => {
      isCancelledRef.current = true;
    };
  }, []);

  return (
    <div className="app-container">
      <img src="/logo_light_unified_noBG.png" alt="Logo" className="top-logo" />
      
      {showNoCreditPopup && (
        <div className="nocredit-popup-overlay">
          <div className="nocredit-confirm-box premium-box">
            
            <div className="nocredit-header">
              <h2>Krediniz Yok</h2>
              <p>Yeni bir projeye başlamak için harika bir zaman!</p>
            </div>
            
            {/* Highlighted Campaign Card */}
            {/*<div className="campaign-highlight">
              <div className="campaign-badge">SON ŞANS • UZATILDI</div>
              <h3>
                Şimdi Sadece <span className="price-tag">25$</span> <span className="vat">+ KDV</span>
              </h3>
              
              <div className="time-limit-container">
                <p className="time-limit">
                  ⏳ Kampanya süresi <strong>{formatDateTR(CAMPAIGN_DEADLINE)}</strong> tarihine kadar uzatıldı.
                </p>
                <div className="countdown-timer">
                  <div className="time-box">
                    <span>{timeLeft.days}</span>
                    <small>Gün</small>
                  </div>
                  <span className="colon">:</span>
                  <div className="time-box">
                    <span>{timeLeft.hours.toString().padStart(2, '0')}</span>
                    <small>Saat</small>
                  </div>
                  <span className="colon">:</span>
                  <div className="time-box">
                    <span>{timeLeft.minutes.toString().padStart(2, '0')}</span>
                    <small>Dk</small>
                  </div>
                  <span className="colon">:</span>
                  <div className="time-box">
                    <span>{timeLeft.seconds.toString().padStart(2, '0')}</span>
                    <small>Sn</small>
                  </div>
                </div>
              </div>
            </div>*/}

            <div className="nocredit-confirm-buttons">
              <button 
                className="btn-buy-now" 
                onClick={() => navigate('/buy_credits', { state: { user_idx } })}
              >
                Hemen Fırsatı Yakala
              </button>
              <button 
                className="btn-cancel-nocredit" 
                onClick={() => setShowNoCreditPopup(false)}
              >
                Belki Daha Sonra
              </button>
            </div>

          </div>
        </div>
      )}

      {error ? (
        <div style={{ color: 'red', padding: 20 }}>
          <h2>Hata</h2>
          <p>{error}</p>
        </div>
      ) : (
        <>
          <SectionsWritingLoader currentStep={currentStep} sectionNames={sectionNames} />
          <ResponseLogger log={responseLog} />
        </>
      )}
    </div>
  );
};

// Utilities
const wait = (ms) => new Promise(res => setTimeout(res, ms));

const runWithRetry = async (fn, min_duration = 0, retryDelay = 30000) => {
  const start = Date.now();
  try {
    const res = await fn();
    const elapsed = Date.now() - start;
    if (elapsed < min_duration) await wait(min_duration - elapsed);
    return res;
  } catch (err) {
    try {
      console.warn("Retrying after failure...");
      await wait(retryDelay);
      const retryStart = Date.now();
      const res = await fn();
      const retryElapsed = Date.now() - retryStart;
      if (retryElapsed < min_duration) await wait(min_duration - retryElapsed);
      return res;
    } catch (err) {
      console.warn("Retrying after failure...");
      await wait(2*retryDelay);
      const retryStart = Date.now();
      const res = await fn();
      const retryElapsed = Date.now() - retryStart;
      if (retryElapsed < min_duration) await wait(min_duration - retryElapsed);
      return res;
    }
  }
};

export default SectionsWriting;
